import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { X, Loader2, Phone, Pencil, Trash2, UserRound } from 'lucide-react';
import { toast } from 'react-toastify';
import { createAdmin, AdminProfileUpdate } from '../../redux/adminSlice';
import { docPatCreationCountryList } from '../../redux/masterSlice';
import { getTenantOptions } from '../../redux/tenantSlice';
import useAxiosAuth from '../../hooks/useAxiosAuth';
import { policyFor, isAnyOneContact } from '../../utils/loginPolicy';
import PhoneCountryDropdown from '../common/PhoneCountryDropdown';
// Used at line ~306 and never imported — the modal threw
// "GenderSelect is not defined" the moment it drew the gender field, so Super
// Admin could not create an admin at all. Same class of omission the file's own
// header records for getGenderOptions.
import GenderSelect from '../common/GenderSelect';
// Used at render time (getGenderOptions) and on every keystroke (formatFirstName
// / formatSurname) but never imported — opening this modal threw
// "getGenderOptions is not defined" before it drew anything. AddPatient,
// AddDoctor, AddTenant and AddFamilyMember all import the same helpers.
import { getGenderOptions, formatFirstName, formatSurname } from '../../utils/personFields';

// Create-admin modal (super_admin). Field set mirrors the doctor create modal
// (profile image, country-aware phone, surname). The backend generates the AD id,
// creates the auth row with a temporary password, and sends the sign-in details
// to the contact(s) given. The create
// endpoint (POST /admins) does NOT accept an image — the image is uploaded in a
// SECOND call to POST /admins/:id/image once we know the new admin id.
const EMPTY = { name: '', surname: '', email: '', phone: '', gender: '', dob: '', description: '', countryId: '', timeZone: '', tenantId: '' };
const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const VALID_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB

// WHICH CONTACTS ARE MANDATORY IS THE TENANT'S LOGIN POLICY (owner, 1 Oct 2026
// — "Concept 2" onboarding). The Super Admin types only what the chosen
// tenant's policy makes mandatory and NO one-time code: the code would go to
// the admin's own phone. The admin proves each contact at their first sign-in
// and chooses their own password there. Under "Mobile no only" an admin is
// created with a mobile number and no e-mail at all.
//
// The server answers 400 { code: 'FIELD_REQUIRED', field } when a mandatory
// contact is missing. These two helpers are the same as the error half of
// Frontend-web/src/utils/loginPolicy.js; they live here rather than in this
// app's utils/loginPolicy.js because that copy is pinned to the tenant app's
// POLICY half only (Frontend-web/src/test/mirroredFiles.test.js).
const POLICY_ERROR_KEYS = {
  FIELD_REQUIRED: {
    email: 'loginPolicy.field_required_email',
    phone: 'loginPolicy.field_required_phone',
  },
  OTP_PROOF_REQUIRED: {
    email: 'loginPolicy.otp_proof_required_email',
    phone: 'loginPolicy.otp_proof_required_phone',
  },
};
const policyErrorField = (field) =>
  ['phone', 'phoneNo', 'phone_no', 'mobile'].includes(field) ? 'phone' : 'email';
const policyErrorMessage = (data, t, lang = 'en') => {
  if (!data || typeof data !== 'object') return null;
  const byField = POLICY_ERROR_KEYS[data.code];
  if (!byField) return null;
  const serverMsg =
    (lang === 'fr' ? data.message?.fr : data.message?.en) ||
    data.message?.en || data.message?.fr ||
    (typeof data.message === 'string' ? data.message : '');
  return t(byField[policyErrorField(data.field)], {
    defaultValue: serverMsg || t('loginPolicy.field_required_generic', 'A required field is missing'),
  });
};

export default function AddAdmin({ isOpen, onClose, onCreated }) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const axiosInstance = useAxiosAuth();
  const mutating = useSelector((s) => s.admin?.mutating);
  const countryTimezoneList = useSelector((s) => s.master?.countryTimezoneList);
  const tenantOptions = useSelector((s) => s.tenant?.tenantOptions);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  const selectedCountry = countryTimezoneList?.find((c) => c.countryId === form.countryId) || null;

  // Every active tenant with its loginPolicy (an authenticated Super Admin is
  // answered the whole list). Fetched each open, like the tenant picker, so a
  // policy changed a moment ago on the tenant form is the one applied here.
  const [publicTenants, setPublicTenants] = useState([]);
  useEffect(() => {
    if (!isOpen) return undefined;
    let alive = true;
    (async () => {
      try {
        const res = await axiosInstance.get('/tenants/public');
        if (alive) setPublicTenants(res?.data?.tenants || []);
      } catch {
        if (alive) setPublicTenants([]);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  const policy = policyFor(publicTenants, form.tenantId);
  const anyOne = isAnyOneContact(policy);
  const emailRequired = !anyOne && !!policy.email.required;
  const phoneRequired = !anyOne && !!policy.mobile.required;

  // Load the country/timezone list once (used by the phone-country picker).
  useEffect(() => {
    if (isOpen && (!countryTimezoneList || countryTimezoneList.length === 0)) {
      dispatch(docPatCreationCountryList());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Tenant list for the picker. Refetched each open so a tenant created moments
  // ago on the Tenants page shows up here without a reload.
  useEffect(() => {
    if (isOpen) dispatch(getTenantOptions());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Revoke the object URL when preview changes / unmount to avoid leaks.
  useEffect(() => {
    return () => { if (imagePreview) URL.revokeObjectURL(imagePreview); };
  }, [imagePreview]);

  if (!isOpen) return null;

  // Canonical person-field casing, same as the doctor and patient forms:
  // first name gets an initial capital, surname is fully uppercase. This form
  // previously stored whatever was typed. The backend now applies the same
  // rules (Backend/utils/personFields.js), so this is for immediate feedback in
  // the field rather than the only place it happens.
  const set = (k, v) => {
    let val = v;
    if (k === 'name') val = formatFirstName(val);
    if (k === 'surname') val = formatSurname(val);
    setForm((f) => ({ ...f, [k]: val }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const reset = () => {
    setForm(EMPTY);
    setErrors({});
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleImagePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!VALID_IMAGE_TYPES.includes(file.type)) {
      toast.warn(t('adminpage.invalid_image_type', 'Please select a JPG, PNG or WEBP image'));
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      toast.warn(t('adminpage.image_too_large', 'Image must be 5MB or smaller'));
      return;
    }
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const removeImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const validate = () => {
    const e = {};
    // Required: an admin with no tenant would see nothing once scoping is on.
    if (!form.tenantId) e.tenantId = t('adminpage.tenant_required', 'Select the tenant this admin belongs to');
    if (!form.name.trim()) e.name = t('adminpage.name_required', 'Name is required');
    const hasEmail = !!form.email.trim();
    const hasPhone = !!form.phone.trim();
    if (!hasEmail) {
      if (emailRequired) e.email = t('adminpage.email_required', 'Email is required');
    } else if (!emailRe.test(form.email.trim())) e.email = t('adminpage.email_invalid', 'Enter a valid email');
    if (!hasPhone && phoneRequired) e.phone = t('adminpage.phone_required', 'Mobile number is required');
    if (anyOne && !hasEmail && !hasPhone) {
      e.contact = t('adminpage.contact_any_one_required', 'Give an e-mail address or a mobile number — this organisation needs at least one.');
    }
    if (hasPhone) {
      if (!form.countryId) {
        e.countryId = t('adminpage.country_required_for_phone', 'Select a country for the phone number');
      } else {
        const min = selectedCountry?.mobileMinLength || 9;
        const max = selectedCountry?.mobileMaxLength || 10;
        const digits = form.phone.trim();
        if (!/^\d+$/.test(digits) || digits.length < min || digits.length > max) {
          e.phone = min === max
            ? t('adminpage.phone_len_exact', { defaultValue: `Phone number must be ${min} digits`, length: min })
            : t('adminpage.phone_len_range', { defaultValue: `Phone number must be between ${min} and ${max} digits`, min, max });
        }
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    try {
      const payload = {
        name: form.name.trim(),
        surname: form.surname.trim(),
        // Blank is sent as null: under "Mobile no only" an admin has no e-mail.
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        gender: form.gender || null,
        dob: form.dob || null,
        description: form.description.trim() || null,
        countryId: form.countryId || null,
        // Which hospital/clinic this admin belongs to. Required by the API —
        // the backend also overrides countryId with the tenant's own country,
        // so an admin can never end up in a different country to their tenant.
        tenantId: form.tenantId || null,
      };
      // 1. Create the admin. Fulfilled payload is { success, message, data: { ...admin, adminId } }.
      const result = await dispatch(createAdmin(payload)).unwrap();
      const newId = result?.data?.adminId;

      // 2. Upload the profile image separately (non-fatal — admin already exists).
      if (imageFile && newId) {
        try {
          const formData = new FormData();
          formData.append('file', imageFile);
          await dispatch(AdminProfileUpdate({ admId: newId, formData })).unwrap();
        } catch {
          toast.warn(t('adminpage.image_upload_failed', 'Admin created, but the profile image could not be uploaded.'));
        }
      }

      toast.success(t('adminpage.create_success', 'Account created. The sign-in details were sent to the contact given.'));
      reset();
      onClose?.();
      onCreated?.();
    } catch (err) {
      // A contact the tenant's policy makes mandatory: show it on that field.
      const policyMsg = policyErrorMessage(err?.data, t, i18n?.language);
      if (policyMsg) {
        setErrors((prev) => ({ ...prev, [policyErrorField(err.data.field)]: policyMsg }));
        return;
      }
      const msg = typeof err?.error === 'string' ? err.error : t('adminpage.create_error', 'Failed to create admin');
      toast.error(msg);
    }
  };

  const field = 'w-full px-3 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]';

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" data-testid="add-admin-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-[#1a365d]">{t('adminpage.add_admin', 'Add Admin')}</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="add-admin-close"><X className="w-5 h-5" /></button>
        </div>

        {/* Profile image upload */}
        <div className="flex flex-col items-center gap-2 pt-5">
          <div className="relative">
            <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-100 border-2 border-[#4ABDBD]/40 flex items-center justify-center">
              {imagePreview ? (
                <img src={imagePreview} alt={t('adminpage.avatar_alt', 'Profile preview')} className="w-full h-full object-cover" data-testid="add-admin-avatar-preview" />
              ) : (
                <UserRound className="w-10 h-10 text-gray-300" />
              )}
            </div>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              data-testid="add-admin-avatar-edit"
              className="absolute bottom-0 right-0 p-1.5 rounded-full bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] text-white shadow-md hover:shadow-lg"
              title={t('adminpage.change_image', 'Change image')}
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            {imagePreview && (
              <button
                type="button"
                onClick={removeImage}
                data-testid="add-admin-avatar-remove"
                className="absolute bottom-0 left-0 p-1.5 rounded-full bg-white text-red-500 border border-gray-200 shadow-md hover:bg-red-50"
                title={t('adminpage.remove_image', 'Remove image')}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid="add-admin-avatar-input"
            onChange={handleImagePick}
          />
          <span className="text-xs text-slate-400">{t('adminpage.profile_photo', 'Profile photo (optional)')}</span>
        </div>

        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Tenant first — it decides the admin's country, so it should be
              chosen before anything country-dependent (like the phone). */}
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-slate-500">{t('adminpage.tenant', 'Tenant')} *</label>
            <select
              data-testid="add-admin-tenant"
              value={form.tenantId}
              onChange={(e) => {
                const tid = e.target.value;
                const picked = (tenantOptions || []).find((x) => x.tenantId === tid);
                // Inherit the tenant's country so the phone field is usable
                // straight away. The backend enforces this too — it always
                // stores the tenant's country regardless of what we send.
                setForm((f) => ({ ...f, tenantId: tid, countryId: picked?.countryId || f.countryId }));
                // A new tenant is a new policy: errors about the contacts no
                // longer describe the form.
                setErrors((er) => ({ ...er, tenantId: undefined, email: undefined, phone: undefined, contact: undefined }));
              }}
              className={`tt-select ${field} ${errors.tenantId ? 'border-red-400' : 'border-gray-300'}`}
            >
              <option value="">{t('adminpage.select_tenant', 'Select a tenant')}</option>
              {(tenantOptions || []).map((tn) => (
                <option key={tn.tenantId} value={tn.tenantId}>{tn.name}</option>
              ))}
            </select>
            {errors.tenantId
              ? <p className="text-xs text-red-500 mt-1">{errors.tenantId}</p>
              : <p className="text-[11px] text-slate-400 mt-1">
                  {t('adminpage.tenant_hint', "The admin inherits this tenant's country.")}
                </p>}
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">{t('adminpage.first_name', 'First name')} *</label>
            <input data-testid="add-admin-name" value={form.name} onChange={(e) => set('name', e.target.value)}
              className={`${field} ${errors.name ? 'border-red-400' : 'border-gray-300'}`} />
            {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">{t('adminpage.surname', 'Surname')}</label>
            <input data-testid="add-admin-surname" value={form.surname} onChange={(e) => set('surname', e.target.value)}
              className={`${field} border-gray-300`} />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500" data-testid="add-admin-email-label">
              {t('adminpage.email', 'Email')}{emailRequired ? ' *' : ''}
            </label>
            <input data-testid="add-admin-email" type="email" value={form.email} onChange={(e) => set('email', e.target.value)}
              className={`${field} ${errors.email ? 'border-red-400' : 'border-gray-300'}`} />
            {errors.email
              ? <p className="text-xs text-red-500 mt-1" data-testid="add-admin-email-error">{errors.email}</p>
              : policy.email.otp && (
                <p className="text-[11px] text-slate-400 mt-1" data-testid="add-admin-email-hint">
                  {t('loginPolicy.verified_at_first_sign_in', 'They will verify this with a one-time code at their first sign-in.')}
                </p>
              )}
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500" data-testid="add-admin-phone-label">
              {t('adminpage.phone', 'Phone')}{phoneRequired ? ' *' : ''}
            </label>
            <div className={`relative flex items-center border rounded-xl px-3 py-2.5 ${!form.countryId ? 'bg-gray-50' : 'bg-white'} ${errors.phone ? 'border-red-400' : 'border-gray-300'} focus-within:ring-2 focus-within:ring-[#4ABDBD]`}>
              <Phone className={`w-4 h-4 mr-2 flex-shrink-0 ${!form.countryId ? 'text-gray-400' : 'text-[#4ABDBD]'}`} />
              <PhoneCountryDropdown
                countryTimezoneList={countryTimezoneList || []}
                countryId={form.countryId}
                onSelect={({ countryId, timeZone, countryName }) => {
                  setForm((f) => ({ ...f, countryId, timeZone, country: countryName, phone: '' }));
                  setErrors((e) => ({ ...e, countryId: undefined, phone: undefined }));
                }}
              />
              <input
                data-testid="add-admin-phone"
                type="tel"
                value={form.phone}
                onChange={(e) => set('phone', e.target.value.replace(/\D/g, ''))}
                maxLength={selectedCountry?.mobileMaxLength || 10}
                disabled={!form.countryId}
                placeholder={!form.countryId ? t('adminpage.select_country_first', 'Select country first') : ''}
                className="w-full bg-transparent focus:outline-none text-sm text-gray-800 placeholder-gray-400 disabled:cursor-not-allowed"
              />
            </div>
            {errors.countryId && <p className="text-xs text-red-500 mt-1">{errors.countryId}</p>}
            {errors.phone && <p className="text-xs text-red-500 mt-1" data-testid="add-admin-phone-error">{errors.phone}</p>}
            {!errors.phone && !errors.countryId && policy.mobile.otp && (
              <p className="text-[11px] text-slate-400 mt-1" data-testid="add-admin-phone-hint">
                {t('loginPolicy.verified_at_first_sign_in', 'They will verify this with a one-time code at their first sign-in.')}
              </p>
            )}
          </div>
          {anyOne && (
            <p className={`sm:col-span-2 -mt-2 text-[11px] ${errors.contact ? 'text-red-500' : 'text-slate-400'}`} data-testid="add-admin-contact-note">
              {errors.contact || t('adminpage.contact_any_one_hint', 'This organisation needs at least one of the two contacts.')}
            </p>
          )}
          <div>
            <label className="text-xs font-medium text-slate-500">{t('adminpage.gender', 'Gender')}</label>
            {/* Was a hand-rolled <select> with only 3 options — it was missing
                "Not Communicated", so an admin could never be recorded with it
                even though doctors and patients could. Use the shared component
                and the canonical list so every person form offers the same
                four values. */}
            <GenderSelect
              data-testid="add-admin-gender"
              value={form.gender}
              onChange={(val) => set('gender', val)}
              options={getGenderOptions(t)}
              placeholder={t('adminpage.select', 'Select')}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">{t('adminpage.dob', 'Date of birth')}</label>
            {/* Cap at today, as AddPatient / AddDoctor / AddFamilyMember all do —
                these two admin forms were the only ones that accepted a date of
                birth in the future. */}
            <input data-testid="add-admin-dob" type="date" value={form.dob} onChange={(e) => set('dob', e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className={`${field} border-gray-300`} />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-slate-500">{t('adminpage.description', 'Description')}</label>
            <textarea data-testid="add-admin-description" rows={2} value={form.description} onChange={(e) => set('description', e.target.value)}
              className={`${field} border-gray-300 resize-none`} />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-gray-300 text-gray-600 hover:bg-gray-50">
            {t('adminpage.cancel', 'Cancel')}
          </button>
          <button type="button" onClick={submit} disabled={mutating} data-testid="add-admin-submit"
            className="px-4 py-2 text-sm rounded-xl text-white font-semibold bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] hover:shadow-lg disabled:opacity-60 flex items-center gap-2">
            {mutating && <Loader2 className="w-4 h-4 animate-spin" />}
            {t('adminpage.create', 'Create admin')}
          </button>
        </div>
      </div>
    </div>
  );
}
